import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2, SlidersHorizontal } from "lucide-react";

const STEP_LABELS = ["Pallino 1", "Pallino 2", "Pallino 3", "Star"];

function ThresholdTable({ title, subtitle, endpoint, testidPrefix }) {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(endpoint).then((r) => setValues(r.data.values)).catch(() => toast.error("Errore caricamento soglie"));
  }, [endpoint]);

  const setCell = (market, i, v) => {
    setValues((prev) => {
      const next = { ...prev, [market]: [...prev[market]] };
      next[market][i] = v;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      for (const [k, arr] of Object.entries(values)) clean[k] = arr.map((x) => parseFloat(x));
      const r = await api.put(endpoint, { values: clean });
      setValues(r.data.values);
      toast.success("Soglie salvate");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio soglie");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const r = await api.delete(endpoint);
    setValues(r.data.values);
    toast.success("Soglie ripristinate ai valori di default");
  };

  return (
    <div data-testid={`${testidPrefix}-editor`} className="bg-black border border-amber-500/15 rounded-md p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-mono-data text-xs text-amber-400/80 tracking-widest uppercase flex items-center gap-2">
            <SlidersHorizontal className="w-3.5 h-3.5" /> {title}
          </h3>
          {subtitle && <p className="text-[10px] text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className="flex gap-2">
          <button
            data-testid={`${testidPrefix}-reset-button`}
            onClick={reset}
            className="flex items-center gap-1 text-[10px] font-mono-data uppercase text-slate-400 border border-slate-600/50 rounded-sm px-2 py-1 hover:text-amber-300 hover:border-amber-500/40 transition-colors"
          >
            <RotateCcw className="w-3 h-3" /> Default
          </button>
          <button
            data-testid={`${testidPrefix}-save-button`}
            onClick={save}
            disabled={saving || !values}
            className="flex items-center gap-1 text-[10px] font-mono-data uppercase text-black bg-[#E3BC55] hover:bg-[#FFD700] rounded-sm px-3 py-1 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Salva Soglie
          </button>
        </div>
      </div>
      {!values ? (
        <p className="font-mono-data text-xs text-amber-500/60">CARICAMENTO…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px]">
            <thead>
              <tr>
                <th className="text-left font-mono-data text-[10px] text-amber-400/70 uppercase tracking-widest px-2 py-1.5">Mercato</th>
                {STEP_LABELS.map((s) => (
                  <th key={s} className="text-center font-mono-data text-[10px] text-amber-400/70 uppercase tracking-widest px-2 py-1.5">{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(values).map(([market, arr]) => (
                <tr key={market} className="border-t border-amber-500/[0.06]">
                  <td className="px-2 py-1.5 text-sm text-white font-heading font-semibold">{market}</td>
                  {arr.map((v, i) => (
                    <td key={i} className="px-2 py-1.5 text-center">
                      <input
                        data-testid={`${testidPrefix}-input-${market.replace(/[\s.]/g, "-").toLowerCase()}-${i}`}
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        value={v}
                        onChange={(e) => setCell(market, i, e.target.value)}
                        className="w-20 bg-black border border-amber-500/25 rounded-sm px-2 py-1 font-mono-data text-xs text-amber-200 text-center focus:outline-none focus:border-amber-400"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ThresholdsEditor() {
  return (
    <div className="space-y-4">
      <ThresholdTable
        title="Soglie Quota per Mercato"
        subtitle="Il pallino richiede che la % pura della quota (1/quota) superi la soglia. Valori crescenti tra 0 e 1."
        endpoint="/admin/thresholds"
        testidPrefix="thresholds"
      />
      <ThresholdTable
        title="Soglie Forma per Mercato"
        subtitle="Seconda condizione: il parametro forma ((statCasa+statTrasferta)/5)/2 deve superare anche questa soglia. Il pallino si accende SOLO se entrambe le condizioni sono soddisfatte."
        endpoint="/admin/form-thresholds"
        testidPrefix="form-thresholds"
      />
    </div>
  );
}
